import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useFormattedDuration } from 'modules/notes/common/components/Timer';
import { roundedDuration } from './utils';

const useTooltipClasses = makeStyles((theme) => ({
  popper: {
    '&.MuiTooltip-popper .MuiTooltip-tooltip': {
      marginTop: theme.spacing(0.15),
      marginBottom: theme.spacing(0.15)
    }
  }
}));

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 1),
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    position: 'relative',
    display: 'flex'
  },
  overTimeIcon: {
    top: 0,
    right: 0,
    position: 'absolute',
    width: theme.spacing(1),
    height: theme.spacing(1),
    border: '4px solid transparent',
    borderTopColor: 'rgba(133, 109, 219, 1)',
    borderRightColor: 'rgba(133, 109, 219, 1)'
  }
}));

type WorkHoursCellProps = {
  actualDuration: number;
  scheduledDuration: number;
};

const WorkHoursCell: React.FC<WorkHoursCellProps> = ({
  actualDuration,
  scheduledDuration
}: WorkHoursCellProps) => {
  const classes = useStyles();
  const tooltip = useTooltipClasses();
  const colorShade = () => {
    if (scheduledDuration > 0) {
      let opacity = actualDuration / scheduledDuration;
      opacity = opacity > 1 ? 1 : opacity;
      return `rgba(38, 210, 147, ${opacity})`;
    } else if (scheduledDuration === 0 && actualDuration > 0) {
      return 'rgba(38, 210, 147, 1)';
    }
    return 'rgba(38, 210, 147, 0)';
  };

  const formattedActualDuration = useFormattedDuration({
    needsMask: true,
    duration: actualDuration ?? 0
  });
  const formattedOvertimeDuration = useFormattedDuration({
    needsMask: true,
    duration: actualDuration - scheduledDuration ?? 0
  });
  const formattedScheduleDuration = useFormattedDuration({
    needsMask: true,
    duration: scheduledDuration ?? 0
  });

  const isOverTime: boolean = useMemo(
    () => roundedDuration(actualDuration) > roundedDuration(scheduledDuration),
    [actualDuration, scheduledDuration]
  );

  const toolTipElement = useMemo(() => {
    return (
      <>
        <Typography variant="caption" data-testid="tooltip-content">
          <FormattedMessage
            id="insights.teamTable.scheduleHours"
            values={{ scheduleHours: formattedScheduleDuration }}
          />
          <br />
          <FormattedMessage
            id="insights.teamTable.actualHours"
            values={{ actualHours: formattedActualDuration }}
          />
          <br />
          <FormattedMessage
            id="insights.teamTable.overtimeHours"
            values={{ overTimeHours: formattedOvertimeDuration }}
          />
        </Typography>
      </>
    );
  }, [
    formattedActualDuration,
    formattedOvertimeDuration,
    formattedScheduleDuration
  ]);

  return (
    <Tooltip
      title={isOverTime ? toolTipElement : ''}
      arrow
      classes={{ popper: tooltip.popper }}
      data-testid="overtime-tooltip"
    >
      <Box
        className={classes.container}
        sx={{
          backgroundColor: colorShade()
        }}
        data-testid={'work-hours-cell-container'}
      >
        {isOverTime && (
          <div
            className={classes.overTimeIcon}
            data-testid={'work-hours-cell-overtime'}
          />
        )}
        <Typography variant="caption" alignSelf="center">
          {formattedActualDuration}
        </Typography>
      </Box>
    </Tooltip>
  );
};
export default React.memo(WorkHoursCell);
